import useKeyHandler from './useKeyHandler';
import groupNodeEngine from '../pages/DashboardComponents/YourNotesTab/Engines/GroupNodeEngine';
import logService from '../services/wss/logService3';

const _name = 'useCrossNodeNav';
const PAGE_SIZE = 25; // must match GroupNodeFetcher.PAGE_SIZE

/**
 * useCrossNodeNav
 *
 * Extends WASD navigation across node boundaries in YourNotes.
 * When the user reaches the top of page 1 or bottom of the last page
 * of the current node, this hook jumps to the previous or next non-empty node.
 *
 * Reads page state directly from groupNodeEngine to avoid stale React state
 * conflicts with useKeyboardNav firing on the same keydown event.
 *
 * Separated from useKeyboardNav because cross-node logic is
 * YourNotes-specific — other lists don't have nodes.
 */
export default function useCrossNodeNav({ activeNode, selectedRk, onSelect }) {

    const jumpToPrevNode = (nodes, nodeIndex) => {
        try {
            if (!nodes || nodes.length === 0) return false;
            for (let i = nodeIndex - 1; i >= 0; i--) {
                const prev = nodes[i]?.entries ?? [];
                if (prev.length > 0) {
                    groupNodeEngine.selectNode(nodes[i].key, 'prev'); // CHANGED — direction prev: selects last entry
                    return true;
                }
            }
            return false;
        } catch (error) {
            logService.logError(`${_name}.jumpToPrevNode: ${error.message}`);
            return false;
        }
    };

    const jumpToNextNode = (nodes, nodeIndex) => {
        try {
            if (!nodes || nodes.length === 0) return false;
            for (let i = nodeIndex + 1; i < nodes.length; i++) {
                const next = nodes[i]?.entries ?? [];
                if (next.length > 0) {
                    groupNodeEngine.selectNode(nodes[i].key, 'next'); // CHANGED — direction next: selects first entry
                    return true;
                }
            }
            return false;
        } catch (error) {
            logService.logError(`${_name}.jumpToNextNode: ${error.message}`);
            return false;
        }
    };

    useKeyHandler({
        onKey: ({ isUp, isDown }) => {
            const nodes = groupNodeEngine.getNodes();
            const currentNode = nodes.find((n) => n.key === activeNode);
            const currentEntries = currentNode?.entries ?? [];
            const currentIndex = currentEntries.findIndex((e) => e.rk === selectedRk);
            if (currentIndex === -1) return;

            // read page from engine directly to avoid stale React state
            const page = groupNodeEngine.getCurrentPage(activeNode) ?? 1;
            const total = currentNode?.total ?? 0;
            const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

            const nodeIndex = nodes.findIndex((n) => n.key === activeNode);

            if (isUp && currentIndex === 0 && page === 1) jumpToPrevNode(nodes, nodeIndex);
            if (isDown && currentIndex === currentEntries.length - 1 && page === totalPages) jumpToNextNode(nodes, nodeIndex);
        },
        deps: [activeNode, selectedRk, onSelect],
    });
}
